/**
 * WCO-2322 (Step 2) — "New Transfer-Back" modal: create an EMPTY container (a raw-material Transfer or an
 * ad-hoc Output) for a chosen destination, on demand — independent of any production session. The container is
 * filled later by SCANNING selected inventory into it. Presentation only; numbering + create logic are backend
 * (POST /:date/new-transfer | /new-output).
 */
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { X, Plus, Loader2, Package, Truck } from 'lucide-react';
import { dailyTransferOutService } from '../../../services/dailyTransferOutService';
import { inventoryService } from '../../../services/inventoryService';
import toast from 'react-hot-toast';

interface NewTransferBackModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  createdBy?: string;
  onCreated?: () => void;
}

type ContainerType = 'transfer' | 'output';

const NewTransferBackModal: React.FC<NewTransferBackModalProps> = ({ isOpen, onClose, date, createdBy, onCreated }) => {
  const { t } = useLanguage();
  const [type, setType] = useState<ContainerType>('transfer');
  const [destination, setDestination] = useState('');
  const [creating, setCreating] = useState(false);
  const [locations, setLocations] = useState<{ location_code: string; description: string }[]>([]);

  useEffect(() => {
    if (isOpen) {
      setType('transfer');
      setDestination('');
      inventoryService.getLocationCodes().then(setLocations).catch(() => setLocations([]));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!destination) return;
    setCreating(true);
    try {
      if (type === 'transfer') {
        const r = await dailyTransferOutService.createNewTransfer(date, destination, createdBy);
        toast.success(`${t('dailyTransferOut.transferCreated')} ${r.transfer_number}`);
      } else {
        const r = await dailyTransferOutService.createAdHocOutput(date, destination, createdBy);
        toast.success(`${t('dailyTransferOut.outputCreated')} ${r.output_number}`);
      }
      onCreated?.();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || t('dailyTransferOut.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const typeBtn = (val: ContainerType, icon: React.ReactNode, label: string) => (
    <button
      onClick={() => setType(val)}
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg border ${
        type === val
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
      }`}
    >
      {icon}{label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('dailyTransferOut.newTransferBack')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('dailyTransferOut.typeLabel')}</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {typeBtn('transfer', <Truck className="w-4 h-4" />, t('dailyTransferOut.transferLabel'))}
              {typeBtn('output', <Package className="w-4 h-4" />, t('dailyTransferOut.outputLabel'))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('dailyTransferOut.destination')}</label>
            <select
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            >
              <option value="">{t('dailyTransferOut.selectDestination')}</option>
              {locations.map((l) => (
                <option key={l.location_code} value={l.location_code}>
                  {l.location_code}{l.description ? ` - ${l.description}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || !destination}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}{t('dailyTransferOut.create')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewTransferBackModal;
